/**
 * Landing-page resolution after authentication.
 *
 * Compass repositioned around budget-first UX in May 2026, so the
 * preference now spans four values across two domains:
 *
 *   • personal_budget     → /budget        (default for new users)
 *   • family_budget       → /family/budget
 *   • personal_patrimony  → /dashboard     (historical default, still honored)
 *   • family_patrimony    → /family
 *
 * The two family-scoped values require a household to be useful. When
 * the calling user doesn't have one (or no longer has one — left the
 * foyer / partner dissolved it), we redirect to a dedicated prompt
 * page that explains the situation and lets them either create a
 * household or fall back to their personal budget WITHOUT mutating
 * the preference in the database. So leaving + re-joining a foyer
 * automatically restores the original behaviour.
 *
 * Anywhere we conditionally surface family entry points (sidebar,
 * dashboard tab, …) keys off the same `canAccessFamilyPage` helper.
 */

const PATHS = {
  personal_budget:    '/budget',
  family_budget:      '/family/budget',
  personal_patrimony: '/dashboard',
  family_patrimony:   '/family',
};

const NO_HOUSEHOLD_FALLBACK = '/family/create-household-prompt';
const ERROR_FALLBACK = '/budget';

const DEFAULT = 'personal_budget';

/**
 * True if the user is allowed to LAND on the family patrimony view.
 * Business rule (kept in sync with SubscriptionService.is_feature_accessible):
 *   1. Trial active → access (lets the user invite their partner)
 *   2. Paid Famille plan → access (same rationale)
 *   3. Any plan with an active family-members entry → read-only OK
 * Refused: gratuit / solo with no family-members entry — they get the
 *          household-prompt page.
 */
export const canAccessFamilyPage = (user) => {
  if (!user) return false;
  if (user.in_trial) return true;
  if (user.plan === 'famille') return true;
  if (user.has_family || user.family_id) return true;
  return false;
};

/**
 * Returns the URL path the calling user should land on. Optional
 * `onFallback(reason)` callback is invoked when we override the user's
 * stated preference so the caller can surface a toast.
 *
 * Reasons emitted:
 *   • 'unknown_preference'   stored value is not in PATHS (shouldn't happen
 *                            because the backend validates, defensive only)
 *   • 'no_household_family_budget'   family_budget preference but no
 *                                    household → redirect to prompt page
 *   • 'no_family_access'             family_patrimony preference but
 *                                    canAccessFamilyPage returned false
 */
export const resolveLandingPath = (user, onFallback) => {
  let preferred;
  try {
    preferred = user?.default_landing_page || DEFAULT;
  } catch {
    // eslint-disable-next-line no-console
    console.warn('[Landing Redirect] error reading preference, using fallback');
    return ERROR_FALLBACK;
  }

  if (!(preferred in PATHS)) {
    // eslint-disable-next-line no-console
    console.info(`[Landing Redirect] target=${PATHS[DEFAULT]} reason=unknown_preference preferred=${preferred}`);
    return PATHS[DEFAULT];
  }

  // ── family_budget → needs a household ──────────────────────────────
  if (preferred === 'family_budget') {
    if (!user?.has_family && !user?.family_id) {
      if (typeof onFallback === 'function') onFallback('no_household_family_budget');
      // eslint-disable-next-line no-console
      console.info(
        `[Landing Redirect] target=${NO_HOUSEHOLD_FALLBACK} reason=family_budget_no_household ` +
        `in_trial=${!!user?.in_trial} plan=${user?.plan || 'none'}`
      );
      return NO_HOUSEHOLD_FALLBACK;
    }
    // eslint-disable-next-line no-console
    console.info(`[Landing Redirect] target=${PATHS.family_budget} reason=user_preference preferred=family_budget`);
    return PATHS.family_budget;
  }

  // ── family_patrimony → needs full family access ────────────────────
  if (preferred === 'family_patrimony') {
    if (!canAccessFamilyPage(user)) {
      if (typeof onFallback === 'function') onFallback('no_family_access');
      // eslint-disable-next-line no-console
      console.info(
        `[Landing Redirect] target=${NO_HOUSEHOLD_FALLBACK} reason=family_inaccessible ` +
        `in_trial=${!!user?.in_trial} plan=${user?.plan || 'none'} ` +
        `has_family=${!!user?.has_family} family_id=${user?.family_id || 'none'}`
      );
      return NO_HOUSEHOLD_FALLBACK;
    }
    // eslint-disable-next-line no-console
    console.info(`[Landing Redirect] target=${PATHS.family_patrimony} reason=user_preference preferred=family_patrimony`);
    return PATHS.family_patrimony;
  }

  // ── personal_budget / personal_patrimony — no preconditions ────────
  // eslint-disable-next-line no-console
  console.info(`[Landing Redirect] target=${PATHS[preferred]} reason=user_preference preferred=${preferred}`);
  return PATHS[preferred];
};

/**
 * Settings UI options, grouped by domain. Budget is listed first per
 * the repositioning. `requiresHousehold` lets the renderer disable
 * the row + show a tooltip when the user has no household yet.
 */
export const LANDING_PAGE_OPTIONS = [
  { value: 'personal_budget',    label: 'Budget personnel',    group: 'budget',    requiresHousehold: false },
  { value: 'family_budget',      label: 'Budget familial',     group: 'budget',    requiresHousehold: true  },
  { value: 'personal_patrimony', label: 'Patrimoine personnel', group: 'patrimony', requiresHousehold: false },
  { value: 'family_patrimony',   label: 'Patrimoine familial',  group: 'patrimony', requiresHousehold: true  },
];

export const LANDING_PAGE_GROUPS = [
  { id: 'budget',    label: 'Budget' },
  { id: 'patrimony', label: 'Patrimoine' },
];

export const LANDING_PAGE_DEFAULT = DEFAULT;
